/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import DateUtil from '../../../../utilities/DateUtil';
import { BodyMain, Small } from '../../../components/Text';
import { BirthDateWithAge } from '../../../localization/LocalizedDate';
import LocalizedGender from '../../../appAuthorized/presenters/LocalizedGender.redux';
import { Grid } from '../../../components/Grid';
import ProfileAvatar from '../../../components/ProfileAvatar';
import ShareCardSummaryGroup from './ShareCardSummaryGroup';
import ProfileModel from '../../../../models/ProfileModel';
import LocalizedBloodType from '../../../appAuthorized/presenters/LocalizedBloodType.redux';
import LocalizedRace from '../../../appAuthorized/presenters/LocalizedRace.redux';
import { LocalizedResidence } from '../../../localization/LocalizedResidence';

export const ShareCardSummaryProfile = ({ shareCard, profile, languageCode }: { shareCard: any, profile: ProfileModel, languageCode: string }) => {
  const group1Items = [
    {
      visible: shareCard.profileName.isIncluded(),
      render: () =>
        <BodyMain>
          {profile.fullName()}
        </BodyMain>,
    },
    {
      visible: shareCard.profileAge.isIncluded(),
      render: () =>
        <BodyMain>
          <BirthDateWithAge
            profile={profile.data}
            today={DateUtil.today()}
            precision={shareCard.birthDatePrecision}
            isMediumFormat
          />
        </BodyMain>,
    },
    {
      visible: shareCard.profileGender.isIncluded(),
      render: () =>
        <BodyMain>
          <LocalizedGender profile={profile} showBorn={shareCard.profileBirthSex.isIncluded()} />
        </BodyMain>,
    },
    {
      visible: !shareCard.profileGender.isIncluded() && shareCard.profileBirthSex.isIncluded(),
      render: () =>
        <BodyMain>
          <LocalizedGender profile={profile} showBorn={shareCard.profileBirthSex.isIncluded()} onlyBorn={true} />
        </BodyMain>,
    },
    {
      visible: shareCard.profileResidence.isIncluded() && !!profile.data.residence_country_id,
      render: () =>
        <BodyMain>
          <LocalizedResidence profile={profile} />
        </BodyMain>,
    },
  ];

  const group2Items = [
    {
      visible: shareCard.profileRace.isIncluded(),
      render: () =>
        <div>
          <div>
            <Small>
              <FormattedMessage id="profile.race.field.label"/>
            </Small>
          </div>
          <div>
            <BodyMain>
              {profile.hasValidRace() ?
                <LocalizedRace
                  profile={profile}
                  primaryClass="cmp-text-body-main"
                  secondaryClass="cmp-text-small font-color-middle-gray"
                />
                :
                <FormattedMessage id="ui-data.data.not-specified.text"/>
              }
            </BodyMain>
          </div>
        </div>,
    },
    {
      visible: shareCard.profileBloodType.isIncluded(),
      render: () =>
        <div>
          <div>
            <Small>
              <FormattedMessage id="profile.bloodType.field.label"/>
            </Small>
          </div>
          <div>
            <BodyMain>
              {profile.hasValidBloodType() ?
                <LocalizedBloodType profile={profile}/>
                :
                <FormattedMessage id="ui-data.data.not-specified.text"/>
              }
            </BodyMain>
          </div>
        </div>,
    },
  ];

  return (
    <div>
      <Group items={group1Items}>
        {
          shareCard.profilePhoto.isIncluded() && profile.hasAvatarPhoto() ? (
            <div>
              <ProfileAvatar profile={profile} hideText={true} />
            </div>
          ) : null
        }
      </Group>
      <Group items={group2Items} childrenSpacing />
    </div>
  );
};

interface CardProfileItem {
  visible: boolean;
  render(): React.ReactFragment;
}

const Group = ({ items, children, childrenSpacing }: { items: CardProfileItem[], children?: React.ReactFragment, childrenSpacing?: boolean }) => {
  const someVisible = items.some(i => i.visible);
  if (!someVisible && !children) { return null; }

  return (
    <ShareCardSummaryGroup>
      <Grid justifyContent="space-between">
        <div>
          {items.map((i, idx) => !i.visible ? null :
            <div
              key={idx}
              style={{ marginTop: childrenSpacing && idx > 0 ? 4 : 0 }}
            >
              {i.render()}
            </div>)
          }
        </div>

        {children}
      </Grid>
    </ShareCardSummaryGroup>
  );
};
